import React, {useRef, useState, useLayoutEffect} from 'react';
import clsx from 'clsx';
import ResizeObserver from 'resize-observer-polyfill';

import {composeClasses, styled} from '@netx/core-components/Theme';

import {ScrollSentinel} from './ScrollSentinel';
import {
	getScrollBorderReactWindowUtilityClass,
	scrollBorderReactWindowClasses,
	ScrollBorderReactWindowClasses,
} from './scrollBorderReactWindowClasses';
import useSharedRef from '../hooks/useSharedRef';
import useEnhancedRef from '../hooks/useEnhancedRef';
import useIntersectionObserver from '../hooks/useIntersectionObserver';

const useUtilityClasses = (ownerState: any) => {
	const {hasOverflow, scrolled, scrolledToEnd, classes} = ownerState;
	const slots = {
		root: [
			'root',
			hasOverflow && 'hasOverflow',
			scrolled && 'scrolled',
			scrolledToEnd && 'scrolledToEnd',
		],
		scrollBorderBottom: ['scrollBorderBottom'],
		scrollBorderTop: ['scrollBorderTop'],
		scrollWrapper: ['scrollWrapper'],
		scrollSentinel: ['scrollSentinel'],
	};
	return composeClasses(
		slots,
		getScrollBorderReactWindowUtilityClass,
		classes as typeof scrollBorderReactWindowClasses,
	);
};

// const ScrollBorderReactWindowRoot = styled('div', {
// 	name: 'ScrollBorderReactWindow',
// 	slot: 'Root',
// })(({theme}) => ({
// 	position: 'relative',
// 	[`&:not(.${scrollBorderReactWindowClasses.scrolled})`]: {
// 		[`& .${scrollBorderReactWindowClasses.scrollBorderTop}`]: {
// 			display: 'none',
// 		},
// 	},
// 	[`&:not(.${scrollBorderReactWindowClasses.hasOverflow}), &.${scrollBorderReactWindowClasses.scrolledToEnd}`]: {
// 		[`& .${scrollBorderReactWindowClasses.scrollBorderBottom}`]: {
// 			display: 'none',
// 		},
// 	},
// }));

const ScrollBorderReactWindowWrapper = styled('div', {
	name: 'ScrollBorderReactWindow',
	slot: 'Wrapper',
})(({theme}) => ({
	borderTop: '1px solid transparent',
}));

const ScrollBorderReactWindowSentinel = styled(ScrollSentinel, {
	name: 'ScrollBorderReactWindow',
	slot: 'ScrollSentinel',
})(({theme}) => ({
	width: '100%',
}));

const ScrollBorderReactWindowBorder = styled('div', {
	name: 'ScrollBorderReactWindow',
	slot: 'ScrollBorder',
})(({theme}) => ({
	transform: 'translate3d(0px, 0px, 0px)',
	position: 'absolute',
	pointerEvents: 'none',
	height: 1,
	width: '100%',
	zIndex: 99,
}));

const ScrollBorderReactWindowBottom = styled(ScrollBorderReactWindowBorder, {
	name: 'ScrollBorderReactWindow',
	slot: 'ScrollBorderBottom',
})(({theme}) => ({
	bottom: 0,
	borderBottom: `1px solid ${theme.palette.custom.border}`,
}));

const ScrollBorderReactWindowTop = styled(ScrollBorderReactWindowBorder, {
	name: 'ScrollBorderReactWindow',
	slot: 'ScrollBorderTop',
})(({theme, ownerState}) => ({
	top: 0,
	borderTop: `1px solid ${theme.palette.custom.border}`,
}));

export interface ScrollBorderReactWindowProps
	extends React.DetailedHTMLProps<
		React.HTMLAttributes<HTMLDivElement>,
		HTMLElement
	> {
	classes?: Partial<ScrollBorderReactWindowClasses>;
	children?: React.ReactNode;
	innerRef?: React.MutableRefObject<HTMLDivElement>;
	onOverflowChange?: (overflown: boolean) => void;
}

export const ScrollBorderReactWindowPrimitive = React.forwardRef<
	HTMLDivElement,
	ScrollBorderReactWindowProps
>((props, ref) => {
	const [scrolled, setScrolled] = useState(false);
	const [scrolledToEnd, setScrolledToEnd] = useState(false);
	const [hasOverflow, setHasOverflow] = useState(false);

	const {
		classes: _classes,
		children,
		className,
		innerRef,
		onOverflowChange,
		...sendProps
	} = props;

	const internalRef = useSharedRef<HTMLDivElement | null>(null, [
		ref,
		innerRef,
	]);
	const [onOverflowChangeRef] = useEnhancedRef(onOverflowChange);

	const ownerState = {
		classes: _classes,
		scrolled,
		scrolledToEnd,
		hasOverflow,
	};

	const classes = useUtilityClasses(ownerState);

	const intersectionObserver = useIntersectionObserver(
		(entries) => {
			if (internalRef.current) {
				setHasOverflow(
					internalRef.current.clientHeight < internalRef.current.scrollHeight,
				);
			}

			entries.forEach((entry) => {
				const end = entry.target.getAttribute('data-id');
				if (end === 'top') {
					setScrolled(
						entry.intersectionRect.y !== entry.boundingClientRect.y &&
							entry.intersectionRatio.toFixed(3) !== '1.000' &&
							!entry.isIntersecting,
					);
				} else if (end === 'bottom') {
					setScrolledToEnd(entry.isIntersecting);
				}
			});
		},
		{
			threshold: 1,
		},
	);

	useLayoutEffect(() => {
		if (!internalRef.current) return;

		const RO = new ResizeObserver(() => {
			if (!internalRef.current) return;
			setHasOverflow(
				internalRef.current.clientHeight < internalRef.current.scrollHeight,
			);
			onOverflowChangeRef.current?.(
				internalRef.current.clientHeight < internalRef.current.scrollHeight,
			);
		});
		RO.observe(internalRef.current);

		setHasOverflow(
			internalRef.current.clientHeight < internalRef.current.scrollHeight,
		);
		onOverflowChangeRef.current?.(
			internalRef.current.clientHeight < internalRef.current.scrollHeight,
		);

		return () => {
			RO.disconnect();
		};
	}, []);

	return (
		<>
			{scrolled ? (
				<ScrollBorderReactWindowTop className={classes.scrollBorderTop} />
			) : null}
			<ScrollBorderReactWindowWrapper
				{...sendProps}
				ref={internalRef}
				className={clsx(classes.scrollWrapper, className)}
			>
				<ScrollBorderReactWindowSentinel
					className={classes.scrollSentinel}
					ref={(node) => intersectionObserver.registerRef(0, node)}
					data-id="top"
				/>
				{children}
				<ScrollBorderReactWindowSentinel
					className={classes.scrollSentinel}
					ref={(node) => intersectionObserver.registerRef(1, node)}
					data-id="bottom"
					style={{marginBottom: 1}}
				/>
			</ScrollBorderReactWindowWrapper>
			{hasOverflow && !scrolledToEnd ? (
				<ScrollBorderReactWindowBottom className={classes.scrollBorderBottom} />
			) : null}
		</>
	);
});

/**
 * @deprecated We can use the `ScrollBorder` component directly instead while using tanstack virtual.
 */
export const ScrollBorderReactWindow = ScrollBorderReactWindowPrimitive;
export default ScrollBorderReactWindow;
